import React from 'react';
import { RouteProps } from 'react-router-dom';
import { RootRoute } from '@summer/react-kit';
import { ConditionalRoute } from 'common/ConditionalRoute';
import { connect } from 'react-redux';
import { AppState } from 'state/reducer';
import {
  isAuthenticatedSelector,
  needMfaEnabledSelector,
  needMfaVerificationSelector,
  needPasswordChangeSelector,
} from 'state/auth/auth.selectors';
import {
  LoginRoute,
  NewPasswordRoute,
  MfaLoginRoute,
  MfaSettingsRoute,
} from 'routes';
import { useRole } from 'common/hooks/useRole';

interface AuthRouteProps extends RouteProps {
  isAuthenticated: boolean;
  needPasswordChange?: boolean | null;
  needMfaVerification?: boolean | null;
  needMfaEnabled?: boolean | null;
}

interface PrivateNewPasswordRouteProps extends RouteProps {
  needPasswordChange: boolean | null;
}

interface PrivateNewMfaLoginRouteProps extends RouteProps {
  needMfaVerification: boolean | null;
}

interface PrivateNewMfaSettingsRouteProps extends RouteProps {
  needMfaEnabled: boolean | null;
}

export const PublicRoute = connect((state: AppState) => ({
  isAuthenticated: isAuthenticatedSelector(state),
}))((props: AuthRouteProps) => (
  <ConditionalRoute
    {...props}
    condition={!props.isAuthenticated}
    redirect={RootRoute}
  />
));

const handlePrivateRouteRedirect: (args: {
  needPasswordChange: boolean;
  needMfaVerification: boolean;
  needMfaEnabled: boolean;
}) => string = ({
  needPasswordChange,
  needMfaVerification,
  needMfaEnabled,
}) => {
  if (needMfaEnabled) {
    return MfaSettingsRoute;
  }

  if (needPasswordChange) {
    return NewPasswordRoute;
  }

  if (needMfaVerification) {
    return MfaLoginRoute;
  }

  return LoginRoute;
};

const handlePrivateRouteCondition = (props: AuthRouteProps) => {
  const {
    needPasswordChange,
    isAuthenticated,
    needMfaVerification,
    needMfaEnabled,
    location,
  } = props;

  if (location?.pathname === MfaSettingsRoute) {
    return isAuthenticated && !needPasswordChange && !needMfaVerification;
  } else {
    return (
      isAuthenticated &&
      !needPasswordChange &&
      !needMfaVerification &&
      !needMfaEnabled
    );
  }
};

export const PrivateRoute = connect((state: AppState) => ({
  isAuthenticated: isAuthenticatedSelector(state),
  needPasswordChange: needPasswordChangeSelector(state),
  needMfaVerification: needMfaVerificationSelector(state),
  needMfaEnabled: needMfaEnabledSelector(state),
}))((props: AuthRouteProps) => {
  const { needPasswordChange, needMfaVerification, needMfaEnabled } = props;

  return (
    <ConditionalRoute
      {...props}
      condition={handlePrivateRouteCondition(props)}
      redirect={handlePrivateRouteRedirect({
        needPasswordChange: !!needPasswordChange,
        needMfaVerification: !!needMfaVerification,
        needMfaEnabled: !!needMfaEnabled,
      })}
    />
  );
});

export const PrivateNewPasswordRoute = connect((state: AppState) => ({
  needPasswordChange: needPasswordChangeSelector(state),
}))((props: PrivateNewPasswordRouteProps) => {
  const { needPasswordChange } = props;

  return (
    <ConditionalRoute
      {...props}
      condition={!!needPasswordChange}
      redirect={RootRoute}
    />
  );
});

export const PrivateMfaLoginRoute = connect((state: AppState) => ({
  needMfaVerification: needMfaVerificationSelector(state),
}))((props: PrivateNewMfaLoginRouteProps) => {
  const { needMfaVerification } = props;

  return (
    <ConditionalRoute
      {...props}
      condition={!!needMfaVerification}
      redirect={RootRoute}
    />
  );
});

export const PrivateMfaSettingsRoute = connect((state: AppState) => ({
  needMfaEnabled: needMfaEnabledSelector(state),
}))((props: PrivateNewMfaSettingsRouteProps) => {
  const { needMfaEnabled } = props;

  return (
    <ConditionalRoute
      {...props}
      condition={!!needMfaEnabled}
      redirect={RootRoute}
    />
  );
});

export const AdministrationRoute = (props: RouteProps) => {
  const { isAdministrationRole } = useRole();

  return (
    <ConditionalRoute
      {...props}
      condition={!!isAdministrationRole}
      redirect={'/'}
    />
  );
};
